import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Flame, Zap, Target, TrendingUp, Brain, Lightbulb,
  Clock, RefreshCw, Send, Volume2, Square, Trophy, AlertTriangle, Layers,
  ClipboardList, Dumbbell, CalendarDays, Coffee, BookOpen, Loader2,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PremiumLockedFeature } from "@/components/PremiumLockedFeature";
import { CoachPersonaPicker } from "@/components/CoachPersonaPicker";
import { PremiumBadge } from "@/components/PremiumBadge";
import { getPersona, loadPersonaId, savePersonaId, withPersona, type CoachPersonaId } from "@/lib/coach-personality";
import { LearningAnalytics } from "@/components/LearningAnalytics";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { getCoachDashboardFn, acceptCoachGoalsFn, askCoachFn } from "@/lib/coach.functions";

export const Route = createFileRoute("/coach")({
  component: CoachPage,
  head: () => ({
    meta: [
      { title: "AI Study Coach - Inquira" },
      { name: "description", content: "Your personal AI study coach: daily goals, insights, smart recommendations and exam readiness predictions." },
      { property: "og:title", content: "AI Study Coach - Inquira" },
      { property: "og:description", content: "Personalized study guidance that learns your habits and keeps you on track." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { property: "og:url", content: "https://www.inquira.online/coach" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/coach" }],
  }),
});

interface CoachReport {
  greeting: string;
  motivation: string;
  insights: Array<{ text: string; kind: "habit" | "strength" | "weakness" | "trend" }>;
  recommendations: Array<{ title: string; reason: string; action: string; subject?: string }>;
  goals: Array<{ title: string; metric: string; target: number; period: "daily" | "weekly" | "monthly"; subject?: string | null }>;
  prediction: { exam_readiness: number; confidence: number; pace: string; risk_of_falling_behind: number; summary: string };
  weakest_subject?: string;
  wins: string[];
}

const ACTION_LINKS: Record<string, { to: string; icon: typeof Layers; label: string }> = {
  flashcards: { to: "/flashcards", icon: Layers, label: "Open flashcards" },
  quiz: { to: "/quiz", icon: ClipboardList, label: "Take a quiz" },
  practice: { to: "/practice", icon: Dumbbell, label: "Practice" },
  solve: { to: "/", icon: BookOpen, label: "Solve a problem" },
  planner: { to: "/planner", icon: CalendarDays, label: "Open planner" },
  review: { to: "/flashcards", icon: RefreshCw, label: "Review" },
  break: { to: "/coach", icon: Coffee, label: "Take a break" },
};

function CoachPage() {
  const { user, isLoading: authLoading, isPaid, streak } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const loadDashboard = useServerFn(getCoachDashboardFn);
  const acceptGoals = useServerFn(acceptCoachGoalsFn);
  const ask = useServerFn(askCoachFn);

  const [snapshot, setSnapshot] = useState<any>(null);
  const [report, setReport] = useState<CoachReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [question, setQuestion] = useState("");
  const [persona, setPersona] = useState<CoachPersonaId>("mentor");
  const [answer, setAnswer] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [goalsAdded, setGoalsAdded] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/login" });
  }, [authLoading, user, navigate]);

  const load = async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const res = await loadDashboard({ data: { refresh } });
      setSnapshot(res.snapshot);
      setReport(res.report as CoachReport);
      setGoalsAdded(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load your coach");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user && isPaid) load(false);
    else if (user) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isPaid]);

  // Stop any narration when leaving the page.
  useEffect(() => () => { audioRef.current?.pause(); audioRef.current = null; }, []);

  // Restore the saved Premium coach personality after hydration.
  useEffect(() => { setPersona(loadPersonaId()); }, []);

  const handleAsk = async (q?: string) => {
    const text = (q ?? question).trim();
    if (text.length < 2 || asking) return;
    setAsking(true);
    setAnswer(null);
    try {
      const res = await ask({ data: { question: withPersona(text, persona), language: language.code } });
      setAnswer(res.answer);
      setQuestion("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "The coach couldn't answer");
    } finally {
      setAsking(false);
    }
  };

  const speak = async (text: string) => {
    if (speaking) {
      audioRef.current?.pause();
      audioRef.current = null;
      setSpeaking(false);
      return;
    }
    setSpeaking(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/text-to-speech`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ text, speed: 1 }),
      });
      if (!response.ok) throw new Error(`Voice unavailable (${response.status})`);
      const blob = await response.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      await audio.play();
    } catch (e) {
      setSpeaking(false);
      toast.error(e instanceof Error ? e.message : "Voice unavailable");
    }
  };

  const handleAcceptGoals = async () => {
    if (!report?.goals?.length) return;
    try {
      await acceptGoals({ data: { goals: report.goals.map((g) => ({ ...g, subject: g.subject ?? null })) } });
      setGoalsAdded(true);
      toast.success("Goals added — they'll track automatically as you study.");
      load(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not add goals");
    }
  };

  if (authLoading || !user) return null;

  if (!isPaid) {
    return (
      <div className="min-h-screen notebook-lines">
        <div className="max-w-lg mx-auto px-4 pb-24">
          <PageHeader />
          <PremiumLockedFeature
            feature="ai_coach"
            title="Meet your AI Study Coach"
            description="A personal mentor that learns your habits, spots weak spots, sets adaptive goals and tells you exactly what to study next — with voice answers whenever you want them."
            icon={<Brain className="size-7 text-neon-pink" />}
          />
        </div>
      </div>
    );
  }

  const p = snapshot?.progress ?? {};
  const today = snapshot?.today ?? { events: 0, xp: 0, minutes: 0 };
  const dailyGoal = p.daily_goal_minutes ?? 25;
  const levelFloor = Math.pow(Math.max(1, (p.level ?? 1) - 1), 2) * 100;
  const levelCeil = Math.pow(p.level ?? 1, 2) * 100;
  const levelPct = levelCeil > levelFloor ? Math.min(100, Math.max(0, ((p.xp - levelFloor) / (levelCeil - levelFloor)) * 100)) : 0;
  const accuracy = p.total_answered > 0 ? Math.round((p.total_correct / p.total_answered) * 100) : null;
  const subjects: Array<any> = snapshot?.subjects ?? [];
  const week: Array<any> = snapshot?.week ?? [];
  const maxWeek = Math.max(...week.map((d) => d.events ?? 0), 1);

  return (
    <div className="min-h-screen notebook-lines">
      <div className="max-w-lg mx-auto px-4 pb-24">
        <PageHeader />

        {loading ? (
          <div className="flex flex-col items-center gap-4 py-16">
            <div className="size-12 rounded-full bg-neon-pink/10 flex items-center justify-center animate-pulse">
              <Brain className="size-6 text-neon-pink" />
            </div>
            <p className="text-sm text-ink-muted">Your coach is reviewing your progress…</p>
          </div>
        ) : error ? (
          <div className="bg-card rounded-2xl p-5 border border-rule text-center space-y-3 mt-4">
            <AlertTriangle className="size-6 text-neon-yellow mx-auto" />
            <p className="text-sm text-ink-muted">{error}</p>
            <Button variant="outline" onClick={() => load(true)}>Try again</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Greeting + motivation */}
            <div className="bg-card rounded-2xl p-5 border border-rule shadow-sm space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <p className="font-[family-name:var(--font-display)] font-extrabold text-ink">
                    {report?.greeting}
                  </p>
                  <p className="text-sm text-ink-muted">{report?.motivation}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => speak(`${report?.greeting} ${report?.motivation}`)}
                    aria-label={speaking ? "Stop coach voice" : "Hear your coach"}
                    className="size-9 rounded-full bg-neon-pink/10 flex items-center justify-center text-neon-pink hover:bg-neon-pink/20 transition-colors"
                  >
                    {speaking ? <Square className="size-4" /> : <Volume2 className="size-4" />}
                  </button>
                  <button
                    onClick={() => load(true)}
                    disabled={refreshing}
                    aria-label="Refresh coaching"
                    className="size-9 rounded-full bg-card border border-rule flex items-center justify-center text-ink-muted hover:bg-rule/30 transition-colors"
                  >
                    <RefreshCw className={`size-4 ${refreshing ? "animate-spin" : ""}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Level + XP */}
            <div className="bg-card rounded-2xl p-5 border border-rule shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-[family-name:var(--font-display)] font-bold text-sm text-ink flex items-center gap-2">
                  <Zap className="size-4 text-neon-yellow" /> Level {p.level ?? 1}
                </span>
                <span className="text-xs font-bold text-neon-pink">{p.xp_to_next ?? 0} XP to next level</span>
              </div>
              <Progress value={levelPct} className="h-3 bg-rule/30" />
              <div className="grid grid-cols-4 gap-2 pt-1">
                <Metric label="XP" value={`${p.xp ?? 0}`} />
                <Metric label="Streak" value={`${streak ?? snapshot?.streak ?? 0}`} />
                <Metric label="Consistency" value={`${p.consistency_score ?? 0}%`} />
                <Metric label="Accuracy" value={accuracy === null ? "—" : `${accuracy}%`} />
              </div>
            </div>

            {/* Today's progress */}
            <div className="bg-card rounded-2xl p-5 border border-rule shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-[family-name:var(--font-display)] font-bold text-sm text-ink flex items-center gap-2">
                  <Clock className="size-4 text-neon-blue" /> Today
                </h2>
                <span className="text-xs text-ink-muted">
                  {today.minutes}/{dailyGoal} min · {today.xp} XP
                </span>
              </div>
              <Progress value={Math.min(100, (today.minutes / dailyGoal) * 100)} className="h-3 bg-rule/30" />
              <p className="text-xs text-ink-muted">
                {today.minutes >= dailyGoal
                  ? "Daily goal complete. Anything else today is a bonus."
                  : `${Math.max(0, dailyGoal - today.minutes)} more minutes to hit today's goal.`}
              </p>
              {snapshot?.flashcards_due > 0 && (
                <Link to="/flashcards" className="text-xs font-semibold text-neon-pink">
                  {snapshot.flashcards_due} flashcards waiting for review →
                </Link>
              )}
            </div>

            {/* Wins */}
            {!!report?.wins?.length && (
              <Card title="Today's wins" icon={<Trophy className="size-4 text-neon-yellow" />}>
                <ul className="space-y-1.5">
                  {report.wins.map((w, i) => (
                    <li key={i} className="text-sm text-ink-muted flex gap-2">
                      <span className="text-neon-yellow">•</span>{w}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Insights */}
            {!!report?.insights?.length && (
              <Card title="AI insights" icon={<Lightbulb className="size-4 text-neon-pink" />}>
                <ul className="space-y-2">
                  {report.insights.map((ins, i) => (
                    <li key={i} className="text-sm text-ink flex gap-2">
                      <span className={`mt-1.5 size-2 rounded-full shrink-0 ${
                        ins.kind === "weakness" ? "bg-neon-yellow"
                        : ins.kind === "strength" ? "bg-neon-blue"
                        : ins.kind === "trend" ? "bg-brand" : "bg-neon-pink"}`} />
                      {ins.text}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Recommendations */}
            {!!report?.recommendations?.length && (
              <Card title="What to do next" icon={<Target className="size-4 text-neon-blue" />}>
                <div className="space-y-2">
                  {report.recommendations.map((rec, i) => {
                    const link = ACTION_LINKS[rec.action] ?? ACTION_LINKS.solve;
                    const Icon = link.icon;
                    return (
                      <Link
                        key={i}
                        to={link.to}
                        className="flex items-start gap-3 rounded-xl border border-rule p-3 hover:bg-rule/20 transition-colors"
                      >
                        <div className="size-8 rounded-lg bg-neon-pink/10 flex items-center justify-center shrink-0">
                          <Icon className="size-4 text-neon-pink" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ink">{rec.title}</p>
                          <p className="text-xs text-ink-muted">{rec.reason}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Smart goals */}
            {!!report?.goals?.length && (
              <Card title="Smart goals" icon={<Target className="size-4 text-neon-yellow" />}>
                <div className="space-y-2">
                  {report.goals.map((g, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl border border-rule px-3 py-2">
                      <span className="text-sm text-ink">{g.title}</span>
                      <span className="text-[11px] uppercase font-bold text-ink-muted">{g.period}</span>
                    </div>
                  ))}
                </div>
                <Button className="w-full mt-3" onClick={handleAcceptGoals} disabled={goalsAdded}>
                  {goalsAdded ? "Goals added" : "Add these goals"}
                </Button>
              </Card>
            )}

            {/* Active goals */}
            {!!snapshot?.goals?.length && (
              <Card title="Active goals" icon={<Flame className="size-4 text-neon-pink" />}>
                <div className="space-y-3">
                  {snapshot.goals.map((g: any) => (
                    <div key={g.id} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-ink">{g.title}</span>
                        <span className="text-xs text-ink-muted">{Math.round(g.progress)}/{Math.round(g.target)}</span>
                      </div>
                      <Progress value={Math.min(100, (g.progress / g.target) * 100)} className="h-2 bg-rule/30" />
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Predictive success */}
            {report?.prediction && (
              <Card title="Predicted success" icon={<TrendingUp className="size-4 text-brand" />}>
                <div className="grid grid-cols-3 gap-2">
                  <Meter label="Exam ready" value={report.prediction.exam_readiness} />
                  <Meter label="Confidence" value={report.prediction.confidence} />
                  <Meter label="Falling behind" value={report.prediction.risk_of_falling_behind} invert />
                </div>
                <p className="text-xs text-ink-muted mt-3">{report.prediction.summary}</p>
              </Card>
            )}

            {/* Weekly activity */}
            {week.length > 0 && (
              <Card title="Learning analytics" icon={<TrendingUp className="size-4 text-neon-blue" />}>
                <div className="flex items-end gap-2 h-24">
                  {week.map((d) => (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold text-ink">{d.events || ""}</span>
                      <div
                        className="w-full rounded-t-lg bg-neon-pink transition-all"
                        style={{ height: `${Math.max(8, (d.events / maxWeek) * 100)}%` }}
                      />
                      <span className="text-[10px] text-ink-muted">
                        {new Date(`${d.day}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" })}
                      </span>
                    </div>
                  ))}
                </div>
                {subjects.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {subjects.map((s) => (
                      <div key={s.subject} className="flex items-center justify-between text-xs">
                        <span className="text-ink truncate">{s.subject}</span>
                        <span className="text-ink-muted">
                          {s.events} actions{s.accuracy !== null ? ` · ${s.accuracy}% accuracy` : ""}
                          {s.prev_7d > 0 ? ` · ${s.last_7d >= s.prev_7d ? "▲" : "▼"}` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {report?.weakest_subject && (
                  <p className="text-xs text-ink-muted mt-3 flex gap-2">
                    <AlertTriangle className="size-4 text-neon-yellow shrink-0" />
                    Weakest area right now: <strong className="text-ink">{report.weakest_subject}</strong>
                  </p>
                )}
              </Card>
            )}

            {/* Ask the coach */}
            <Card title="Ask your coach" icon={<Brain className="size-4 text-neon-pink" />}>
              <div className="mb-3 space-y-2">
                <div className="flex items-center gap-2">
                  <PremiumBadge label="Premium coach" size="xs" />
                  <span className="text-[11px] text-ink-muted">
                    Speaking as {getPersona(persona).emoji} {getPersona(persona).name}
                  </span>
                </div>
                <CoachPersonaPicker
                  value={persona}
                  onChange={(id) => {
                    setPersona(id);
                    savePersonaId(id);
                  }}
                />
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {["What should I study today?", "What are my weakest subjects?", "How can I improve?"].map((q) => (
                  <button
                    key={q}
                    onClick={() => handleAsk(q)}
                    disabled={asking}
                    className="text-xs rounded-full border border-rule px-3 py-1.5 text-ink-muted hover:bg-rule/30 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAsk()}
                  placeholder="Ask anything about your studying…"
                  aria-label="Ask your study coach a question"
                  maxLength={500}
                />
                <Button onClick={() => handleAsk()} disabled={asking || question.trim().length < 2} aria-label="Send question">
                  {asking ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                </Button>
              </div>
              {answer && (
                <div className={`mt-3 space-y-2 rounded-xl border border-neon-yellow/30 bg-gradient-to-br ${getPersona(persona).accent} p-3`}>
                  <p className="text-[11px] font-bold text-ink-muted">
                    {getPersona(persona).emoji} {getPersona(persona).name}
                  </p>
                  <p className="text-sm text-ink">{answer}</p>
                  <button
                    onClick={() => speak(answer)}
                    className="text-xs font-semibold text-neon-pink flex items-center gap-1.5"
                  >
                    {speaking ? <Square className="size-3" /> : <Volume2 className="size-3" />}
                    {speaking ? "Stop" : "Hear it"}
                  </button>
                </div>
              )}
            </Card>

            <LearningAnalytics />
          </div>
        )}
      </div>
    </div>
  );
}

function PageHeader() {
  return (
    <header className="flex items-center gap-3 py-4">
      <Link to="/" aria-label="Back to home" title="Back to home" className="size-9 rounded-full bg-card border border-rule flex items-center justify-center hover:bg-rule/30 transition-colors">
        <ArrowLeft className="size-4 text-ink-muted" />
      </Link>
      <h1 className="font-[family-name:var(--font-display)] font-extrabold text-xl text-ink">
        AI Study Coach
      </h1>
      <Link
        to="/exam"
        className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-card border border-rule text-xs font-semibold text-ink hover:bg-rule/30 transition-colors"
      >
        <CalendarDays className="size-4 text-neon-purple" /> Exam Mode
      </Link>
    </header>
  );
}

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="bg-card rounded-2xl p-5 border border-rule shadow-sm">
      <h2 className="font-[family-name:var(--font-display)] font-bold text-sm text-ink mb-3 flex items-center gap-2">
        {icon} {title}
      </h2>
      {children}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <p className="text-lg font-extrabold font-[family-name:var(--font-display)] text-ink">{value}</p>
      <p className="text-[10px] text-ink-muted">{label}</p>
    </div>
  );
}

function Meter({ label, value, invert }: { label: string; value: number; invert?: boolean }) {
  const good = invert ? value < 40 : value >= 60;
  return (
    <div className="text-center space-y-1">
      <p className={`text-xl font-extrabold font-[family-name:var(--font-display)] ${good ? "text-neon-blue" : "text-neon-yellow"}`}>
        {value}%
      </p>
      <p className="text-[10px] text-ink-muted">{label}</p>
      <Progress value={value} className="h-1.5 bg-rule/30" />
    </div>
  );
}
